import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AutoAwesome as AutoAwesomeIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { AddressService, Address } from '../../../services/addressService';
import toast from 'react-hot-toast';
import AddressMapView from './AddressMapView';
import SimpleMapView from './SimpleMapView';
import MapSearchNew from '../../MapSearch/MapSearchNew';
import { useTranslation } from '../../../contexts/TranslationContext';

interface AddressesTabProps {
  prefilledAddress?: Partial<Address>;
  onAddressAdded?: (address: Address) => void;
  readonlyFields?: boolean; // When true, pre-filled fields become read-only
  coordinates?: { lat: number; lng: number }; // Optional coordinates to display
}

const AddressesTab: React.FC<AddressesTabProps> = ({ prefilledAddress, onAddressAdded, readonlyFields = false, coordinates }) => {
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingAddress, setAddingAddress] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [showMapView, setShowMapView] = useState(false);
  const [useSimpleMap, setUseSimpleMap] = useState(false);
  const [mapSearchOpen, setMapSearchOpen] = useState(false);
  const [pendingAddress, setPendingAddress] = useState<Partial<Address> | null>(null);
  const [addressValidatedViaMap, setAddressValidatedViaMap] = useState(false);
  const [newAddress, setNewAddress] = useState<Partial<Address>>({
    title: '',
    full_name: '',
    company: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: '',
    is_default: false,
  });

  useEffect(() => {
    loadAddresses();
  }, []);

  // Pre-fill form when prefilledAddress is provided
  useEffect(() => {
    if (prefilledAddress) {
      console.log('🔍 Pre-filling address with:', prefilledAddress);
      console.log('🔍 Coordinates received:', coordinates);
      setNewAddress({
        title: prefilledAddress.title || 'Map Location',
        full_name: prefilledAddress.full_name || '',
        company: prefilledAddress.company || '',
        address_line_1: prefilledAddress.address_line_1 || '',
        address_line_2: prefilledAddress.address_line_2 || '',
        city: prefilledAddress.city || '',
        state: prefilledAddress.state || '',
        postal_code: prefilledAddress.postal_code || '',
        country: prefilledAddress.country || '',
        phone: prefilledAddress.phone || '',
        is_default: prefilledAddress.is_default || false,
      });
      setAddDialogOpen(true);
    }
  }, [prefilledAddress, coordinates]);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      const data = await AddressService.getUserAddresses();
      setAddresses(data);
    } catch (error: any) {
      console.error('Error loading addresses:', error);
      toast.error(t('settings.addresses.toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async () => {
    try {
      // Check if this is a manually entered address (not from map selection or validation)
      if (!coordinates && !readonlyFields && !addressValidatedViaMap) {
        // Validate manually entered address by opening map search
        const addressString = `${newAddress.address_line_1}, ${newAddress.city}, ${newAddress.state} ${newAddress.postal_code}, ${newAddress.country}`;
        
        // Store the pending address for after validation
        setPendingAddress({
          ...newAddress,
        });
        
        // Open map search for validation
        setMapSearchOpen(true);
        return;
      }
      
      // If coordinates are available or it's a map-selected address, proceed directly
      setAddingAddress(true);
      
      const addressData = {
        ...newAddress,
        ...(coordinates && {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        }),
      };
      
      const addedAddress = await AddressService.addAddress(addressData as Omit<Address, 'id' | 'created_at' | 'updated_at'>);
      toast.success(t('settings.addresses.toast.added'));
      setAddDialogOpen(false);
      
      // Call the callback to notify parent component
      if (onAddressAdded && addedAddress) {
        onAddressAdded(addedAddress);
      }
      
      setNewAddress({
        title: '',
        full_name: '',
        company: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        phone: '',
        is_default: false,
      });
      setAddressValidatedViaMap(false);
      loadAddresses();
    } catch (error: any) {
      console.error('Error adding address:', error);
      toast.error(t('settings.addresses.toast.addFailed'));
    } finally {
      setAddingAddress(false);
    }
  };

  const handleEditAddress = async () => {
    if (!editingAddress) return;
    
    try {
      await AddressService.updateAddress(editingAddress.id, editingAddress);
      toast.success(t('settings.addresses.toast.updated'));
      setEditDialogOpen(false);
      setEditingAddress(null);
      loadAddresses();
    } catch (error: any) {
      console.error('Error updating address:', error);
      toast.error(t('settings.addresses.toast.updateFailed'));
    }
  };

  const handleDeleteAddress = async (id: string) => {
    if (!window.confirm(t('settings.addresses.toast.deleteConfirm'))) return;
    
    try {
      await AddressService.deleteAddress(id);
      toast.success(t('settings.addresses.toast.deleted'));
      loadAddresses();
    } catch (error: any) {
      console.error('Error deleting address:', error);
      toast.error(t('settings.addresses.toast.deleteFailed'));
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await AddressService.setDefaultAddress(id);
      toast.success(t('settings.addresses.toast.defaultUpdated'));
      loadAddresses();
    } catch (error: any) {
      console.error('Error setting default address:', error);
      toast.error(t('settings.addresses.toast.defaultFailed'));
    }
  };

  const handleMapAddressSelect = (addressData: {
    address_line_1: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    latitude: number;
    longitude: number;
  }) => {
    // Pre-fill the new address form with map data
    setNewAddress({
      title: t('settings.addresses.mapLocation'),
      full_name: '',
      company: '',
      address_line_1: addressData.address_line_1,
      address_line_2: '',
      city: addressData.city,
      state: addressData.state,
      postal_code: addressData.postal_code,
      country: addressData.country,
      phone: '',
      is_default: addresses.length === 0,
    });
    
    // Close map view and open add dialog
    setShowMapView(false);
    setAddDialogOpen(true);
    
    toast.success(t('settings.addresses.toast.mapSelected'));
  };

  // Handle map search validation for manually entered addresses
  const handleMapSearchValidation = (location: any) => {
    if (location && pendingAddress) {
      // Address was found and validated - return to form instead of saving
      const validatedAddress = {
        ...pendingAddress,
        latitude: location.lat,
        longitude: location.lng,
      };
      
      // Update the form with verified address data
      setNewAddress(validatedAddress);
      
      // Set validation state
      setAddressValidatedViaMap(true);
      
      // Close map search
      setMapSearchOpen(false);
      setPendingAddress(null);
      
      toast.success(t('settings.addresses.toast.mapVerifySuccess'), {
        duration: 4000,
        style: {
          background: '#4caf50',
          color: '#fff',
          fontSize: '14px',
          fontWeight: 'bold',
        },
        icon: '✅',
      });
    } else {
      // Address not found - show warning
      toast.error(t('settings.addresses.toast.mapVerifyFail'), {
        duration: 5000,
        style: {
          background: '#ff6b35',
          color: '#fff',
          fontSize: '14px',
          fontWeight: 'bold',
        },
        icon: '⚠️',
      });
    }
  };

  const saveValidatedAddress = async (addressData: Partial<Address>) => {
    try {
      const addedAddress = await AddressService.addAddress(addressData as Omit<Address, 'id' | 'created_at' | 'updated_at'>);
      
      // Close add dialog
      setAddDialogOpen(false);
      
      // Call the callback to notify parent component
      if (onAddressAdded && addedAddress) {
        onAddressAdded(addedAddress);
      }
      
      // Reset form
    setNewAddress({
        title: '',
        full_name: '',
        company: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        phone: '',
        is_default: false,
      });
      
      loadAddresses();
    } catch (error: any) {
      console.error('Error saving validated address:', error);
      toast.error('Failed to save address');
    }
  };

  const generateRandomAddress = async () => {
    try {
      // Show loading state
      toast.loading(t('settings.addresses.toast.gen.loading'), { id: 'generating-address' });
      
      // Real German address data - these are actual existing addresses
      const realGermanAddresses = [
        // Berlin addresses
        {
          title: t('settings.addresses.addressTitle.home'),
          full_name: 'Max Mustermann',
          company: '',
          address_line_1: 'Unter den Linden 1',
          address_line_2: '',
          city: 'Berlin',
          state: 'Berlin',
          postal_code: '10117',
          country: 'Deutschland',
          phone: '+49 30 12345678',
          is_default: addresses.length === 0,
        },
        {
          title: t('settings.addresses.addressTitle.work'),
          full_name: 'Anna Schmidt',
          company: 'Tech Solutions GmbH',
          address_line_1: 'Potsdamer Platz 10',
          address_line_2: 'Etage 5',
          city: 'Berlin',
          state: 'Berlin',
          postal_code: '10785',
          country: 'Deutschland',
          phone: '+49 30 98765432',
          is_default: addresses.length === 0,
        },
        {
          title: t('settings.addresses.addressTitle.office'),
          full_name: 'Thomas Weber',
          company: 'Design Studio Berlin',
          address_line_1: 'Kurfürstendamm 26',
          address_line_2: '',
          city: 'Berlin',
          state: 'Berlin',
          postal_code: '10719',
          country: 'Deutschland',
          phone: '+49 30 55555555',
          is_default: addresses.length === 0,
        },
        // Munich addresses
        {
          title: t('settings.addresses.addressTitle.home'),
          full_name: 'Maria Müller',
          company: '',
          address_line_1: 'Marienplatz 8',
          address_line_2: 'Wohnung 12',
          city: 'München',
          state: 'Bayern',
          postal_code: '80331',
          country: 'Deutschland',
          phone: '+49 89 12345678',
          is_default: addresses.length === 0,
        },
        {
          title: t('settings.addresses.addressTitle.work'),
          full_name: 'Hans Fischer',
          company: 'Bavaria Tech AG',
          address_line_1: 'Leopoldstraße 15',
          address_line_2: 'Büro 301',
          city: 'München',
          state: 'Bayern',
          postal_code: '80802',
          country: 'Deutschland',
          phone: '+49 89 98765432',
          is_default: addresses.length === 0,
        },
        // Hamburg addresses
        {
          title: t('settings.addresses.addressTitle.home'),
          full_name: 'Petra Hoffmann',
          company: '',
          address_line_1: 'Reeperbahn 1',
          address_line_2: '',
          city: 'Hamburg',
          state: 'Hamburg',
          postal_code: '20359',
          country: 'Deutschland',
          phone: '+49 40 12345678',
          is_default: addresses.length === 0,
        },
        {
          title: t('settings.addresses.addressTitle.work'),
          full_name: 'Klaus Wagner',
          company: 'Hamburg Solutions',
          address_line_1: 'Jungfernstieg 16',
          address_line_2: '3. Stock',
          city: 'Hamburg',
          state: 'Hamburg',
          postal_code: '20354',
          country: 'Deutschland',
          phone: '+49 40 98765432',
          is_default: addresses.length === 0,
        },
        // Cologne addresses
        {
          title: t('settings.addresses.addressTitle.home'),
          full_name: 'Sabine Klein',
          company: '',
          address_line_1: 'Domplatz 1',
          address_line_2: 'Wohnung 8',
          city: 'Köln',
          state: 'Nordrhein-Westfalen',
          postal_code: '50667',
          country: 'Deutschland',
          phone: '+49 221 12345678',
          is_default: addresses.length === 0,
        },
        {
          title: t('settings.addresses.addressTitle.work'),
          full_name: 'Michael Braun',
          company: 'Köln Digital GmbH',
          address_line_1: 'Hohe Straße 20',
          address_line_2: '',
          city: 'Köln',
          state: 'Nordrhein-Westfalen',
          postal_code: '50667',
          country: 'Deutschland',
          phone: '+49 221 98765432',
          is_default: addresses.length === 0,
        },
        // Frankfurt addresses
        {
          title: t('settings.addresses.addressTitle.home'),
          full_name: 'Lisa Zimmermann',
          company: '',
          address_line_1: 'Zeil 1',
          address_line_2: '',
          city: 'Frankfurt am Main',
          state: 'Hessen',
          postal_code: '60313',
          country: 'Deutschland',
          phone: '+49 69 12345678',
          is_default: addresses.length === 0,
        },
        {
          title: t('settings.addresses.addressTitle.work'),
          full_name: 'Andreas Richter',
          company: 'Frankfurt Finance AG',
          address_line_1: 'Mainzer Landstraße 123',
          address_line_2: 'Etage 15',
          city: 'Frankfurt am Main',
          state: 'Hessen',
          postal_code: '60327',
          country: 'Deutschland',
          phone: '+49 69 98765432',
          is_default: addresses.length === 0,
        },
        // Stuttgart addresses
        {
          title: t('settings.addresses.addressTitle.home'),
          full_name: 'Julia König',
          company: '',
          address_line_1: 'Königstraße 1',
          address_line_2: 'Wohnung 5',
          city: 'Stuttgart',
          state: 'Baden-Württemberg',
          postal_code: '70173',
          country: 'Deutschland',
          phone: '+49 711 12345678',
          is_default: addresses.length === 0,
        },
        {
          title: t('settings.addresses.addressTitle.work'),
          full_name: 'Stefan Berger',
          company: 'Stuttgart Engineering',
          address_line_1: 'Schillerstraße 25',
          address_line_2: '',
          city: 'Stuttgart',
          state: 'Baden-Württemberg',
          postal_code: '70173',
          country: 'Deutschland',
          phone: '+49 711 98765432',
          is_default: addresses.length === 0,
        }
      ];
      
      // Select a random real German address
      const randomAddress = realGermanAddresses[Math.floor(Math.random() * realGermanAddresses.length)];
      
      setNewAddress(randomAddress);
      toast.success(t('settings.addresses.toast.gen.success'), { id: 'generating-address' });
      
    } catch (error) {
      console.error('Error generating German address:', error);
      toast.error(t('settings.addresses.toast.gen.failed'), { id: 'generating-address' });
    }
  };

  const getCountryName = (countryCode: string): string => {
    const countryMap: { [key: string]: string } = {
      'US': t('settings.addresses.country.us'),
      'CA': t('settings.addresses.country.ca'),
      'GB': t('settings.addresses.country.gb'),
      'AU': t('settings.addresses.country.au'),
      'DE': t('settings.addresses.country.de'),
      'FR': t('settings.addresses.country.fr'),
      'IT': t('settings.addresses.country.it'),
      'ES': t('settings.addresses.country.es'),
      'NL': t('settings.addresses.country.nl'),
      'SE': t('settings.addresses.country.se'),
      'NO': t('settings.addresses.country.no'),
      'DK': t('settings.addresses.country.dk'),
    };
    return countryMap[countryCode] || t('settings.addresses.country.us');
  };

  const generateRandomAddressData = () => {
    // Return a fallback real German address for edit dialog
    const fallbackGermanAddresses = [
      {
        title: t('settings.addresses.addressTitle.home'),
        full_name: 'Max Mustermann',
        company: '',
        address_line_1: 'Unter den Linden 1',
        address_line_2: '',
        city: 'Berlin',
        state: 'Berlin',
        postal_code: '10117',
        country: 'Deutschland',
        phone: '+49 30 12345678',
      },
      {
        title: t('settings.addresses.addressTitle.work'),
        full_name: 'Anna Schmidt',
        company: 'Tech Solutions GmbH',
        address_line_1: 'Potsdamer Platz 10',
        address_line_2: 'Etage 5',
        city: 'Berlin',
        state: 'Berlin',
        postal_code: '10785',
        country: 'Deutschland',
        phone: '+49 30 98765432',
      },
      {
        title: t('settings.addresses.addressTitle.office'),
        full_name: 'Maria Müller',
        company: 'Bavaria Tech AG',
        address_line_1: 'Marienplatz 8',
        address_line_2: 'Wohnung 12',
        city: 'München',
        state: 'Bayern',
        postal_code: '80331',
        country: 'Deutschland',
        phone: '+49 89 12345678',
      }
    ];
    
    return fallbackGermanAddresses[Math.floor(Math.random() * fallbackGermanAddresses.length)];
  };

  const getAddressIcon = (title: string) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('home') || lowerTitle.includes('house')) {
      return <HomeIcon />;
    } else if (lowerTitle.includes('work') || lowerTitle.includes('office') || lowerTitle.includes('business')) {
      return <BusinessIcon />;
    } else {
      return <LocationIcon />;
    }
  };

  const formatAddress = (address: Address) => {
    const parts = [
      address.address_line_1,
      address.address_line_2,
      `${address.city}, ${address.state} ${address.postal_code}`,
      address.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show map view if enabled
  if (showMapView) {
    return useSimpleMap ? (
      <SimpleMapView 
        onBack={() => setShowMapView(false)}
        onAddressSelect={handleMapAddressSelect}
      />
    ) : (
      <AddressMapView 
        onBack={() => setShowMapView(false)}
        onAddressSelect={handleMapAddressSelect}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', md: 'flex-start' }, 
        mb: { xs: 2.5, md: 4 },
        gap: { xs: 1.5, md: 2 },
        flexDirection: { xs: 'column', md: 'row' }
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: { xs: 0.5, md: 1 }
            }}
          >
            {t('settings.addresses.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' } }}>
            {t('settings.addresses.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, auto)' }, 
          gap: { xs: 1, md: 2 }, 
          alignItems: 'center' 
        }}>
          <Button
            variant="outlined"
            startIcon={<MapIcon />}
            onClick={() => {
              setUseSimpleMap(false);
              setShowMapView(true);
            }}
            sx={{
              borderRadius: 2,
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 },
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: { xs: '0.9rem', md: '1rem' },
              border: '2px solid #667eea',
              color: '#667eea',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                border: '2px solid #5a6fd8',
                color: '#5a6fd8',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {t('settings.addresses.buttons.findOnMap')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<LocationIcon />}
            onClick={() => {
              setUseSimpleMap(true);
              setShowMapView(true);
            }}
            sx={{
              borderRadius: 2,
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 },
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: { xs: '0.9rem', md: '1rem' },
              border: '2px solid #4caf50',
              color: '#4caf50',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)',
                border: '2px solid #388e3c',
                color: '#388e3c',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {t('settings.addresses.buttons.searchAddress')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              px: { xs: 2, md: 4 },
              py: { xs: 1, md: 1.5 },
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: { xs: '0.9rem', md: '1rem' },
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {t('settings.addresses.buttons.addAddress')}
          </Button>
        </Box>
      </Box>

      {addresses.length === 0 ? (
        <Card sx={{ 
          textAlign: 'center', 
          py: 8, 
          px: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.02) 100%)',
          border: '1px solid rgba(102, 126, 234, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}>
          <CardContent>
            <Box sx={{ 
              display: 'inline-flex',
              p: 3,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              mb: 3
            }}>
              <LocationIcon sx={{ fontSize: 48, color: '#667eea' }} />
            </Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold',
                mb: 1,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('settings.addresses.empty.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
              {t('settings.addresses.empty.subtitle')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '1rem',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {t('settings.addresses.empty.button')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: { xs: 2, md: 3 } }}>
          {addresses.map((address) => (
            <Box key={address.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  border: address.is_default ? '2px solid #667eea' : '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: 3,
                  background: '#ffffff',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  '&:hover': {
                    boxShadow: '0 6px 22px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)',
                    border: address.is_default ? '2px solid #667eea' : '1px solid rgba(102,126,234,0.3)',
                  },
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {address.is_default && (
                  <Chip
                    icon={<StarIcon />}
                    label={t('settings.addresses.chip.default')}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      zIndex: 1,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                    }}
                  />
                )}
                
                <CardContent sx={{ pt: address.is_default ? 6 : 3, pb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
                    <Avatar
                      sx={{
                        background: address.is_default 
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                        color: address.is_default ? 'white' : 'grey.600',
                        mr: 2.5,
                        width: { xs: 44, md: 48 },
                        height: { xs: 44, md: 48 },
                        boxShadow: address.is_default 
                          ? '0 4px 15px rgba(102, 126, 234, 0.3)'
                          : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      {getAddressIcon(address.title)}
                    </Avatar>
                    <Box sx={{ flex: 1, minHeight: 48, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                          mb: 0.5,
                          color: address.is_default ? 'primary.main' : 'text.primary'
                        }}
                      >
                        {address.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          fontWeight: 500
                        }}
                      >
                        {address.full_name}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 3, 
                      lineHeight: 1.6,
                      color: 'text.primary',
                      fontSize: '0.95rem'
                    }}
                  >
                    {formatAddress(address)}
                  </Typography>

                  {address.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <PhoneIcon sx={{ fontSize: 18, mr: 1.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {address.phone}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.08)' }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', sm: 'auto auto' }, gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setEditingAddress(address);
                          setEditDialogOpen(true);
                        }}
                        sx={{
                          flex: 1,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 'bold',
                          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)',
                          color: '#4caf50',
                          border: '1px solid rgba(76, 175, 80, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(139, 195, 74, 0.15) 100%)',
                          },
                        }}
                      >
                        {t('settings.addresses.actions.edit')}
                      </Button>
                      <IconButton
                        onClick={() => handleDeleteAddress(address.id)}
                        sx={{ 
                          color: '#e53935',
                          border: '1px solid rgba(229, 57, 53, 0.3)',
                          borderRadius: 2,
                          '&:hover': {
                            background: 'rgba(229, 57, 53, 0.08)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {/* Add Address Dialog */}
      <Dialog open={addDialogOpen} onClose={() => {
        setAddDialogOpen(false);
        setAddressValidatedViaMap(false);
      }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('settings.addresses.dialog.add.title')}
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={generateRandomAddress}
            sx={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              color: '#667eea',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                border: '1px solid rgba(102, 126, 234, 0.5)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {t('settings.addresses.dialog.generate')}
          </Button>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Success Notification for Map-Validated Address */}
            {(() => {
              console.log(' Notification check:', { coordinates, addressValidatedViaMap, readonlyFields, shouldShow: coordinates || addressValidatedViaMap || readonlyFields });
              return coordinates || addressValidatedViaMap || readonlyFields;
            })() && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                p: 2,
                backgroundColor: '#e8f5e8',
                borderRadius: 2,
                border: '2px solid #4caf50',
                mb: 2,
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  fontSize: '20px',
                }}>
                  ✅
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ 
                    color: '#2e7d32', 
                    fontWeight: 'bold',
                    mb: 0.5,
                  }}>
                    {t('settings.addresses.successFound.title')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                    {coordinates 
                      ? t('settings.addresses.successFound.text.verified')
                      : readonlyFields
                      ? t('settings.addresses.successFound.text.selected')
                      : t('settings.addresses.successFound.text.validated')
                    }
                  </Typography>
                  {coordinates && (
                    <Typography variant="caption" sx={{ 
                      color: '#2e7d32', 
                      fontFamily: 'monospace',
                      display: 'block',
                      mt: 0.5,
                    }}>
                      📍 {t('settings.addresses.coords.title')}: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
                    </Typography>
                  )}
                  {readonlyFields && !coordinates && (
                    <Typography variant="caption" sx={{ 
                      color: '#2e7d32', 
                      fontFamily: 'monospace',
                      display: 'block',
                      mt: 0.5,
                    }}>
                      📍 {t('settings.addresses.mapSelectedLocation')}
                    </Typography>
                  )}
                  {addressValidatedViaMap && newAddress.latitude && newAddress.longitude && (
                    <Typography variant="caption" sx={{ 
                      color: '#2e7d32', 
                      fontFamily: 'monospace',
                      display: 'block',
                      mt: 0.5,
                    }}>
                      📍 {t('settings.addresses.coords.title')}: {newAddress.latitude.toFixed(6)}, {newAddress.longitude.toFixed(6)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            
            <TextField
              label="Address Title"
              placeholder={t('settings.addresses.fields.addressTitle.ph')}
              value={newAddress.title}
              onChange={(e) => setNewAddress({ ...newAddress, title: e.target.value })}
              fullWidth
              required
              disabled={readonlyFields && !!prefilledAddress?.title}
              sx={{
                ...(readonlyFields && prefilledAddress?.title && {
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    cursor: 'not-allowed',
                  },
                  '& .MuiInputBase-root': {
                    backgroundColor: '#f5f5f5',
                  },
                }),
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('settings.addresses.fields.fullName')}
                value={newAddress.full_name}
                onChange={(e) => setNewAddress({ ...newAddress, full_name: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label={t('settings.addresses.fields.company')}
                value={newAddress.company}
                onChange={(e) => setNewAddress({ ...newAddress, company: e.target.value })}
                fullWidth
              />
            </Box>

            <TextField
              label={t('settings.addresses.fields.address1')}
              placeholder={t('settings.addresses.fields.address1.ph')}
              value={newAddress.address_line_1}
              onChange={(e) => setNewAddress({ ...newAddress, address_line_1: e.target.value })}
              fullWidth
              required
              disabled={readonlyFields && !!prefilledAddress?.address_line_1}
              sx={{
                ...(readonlyFields && prefilledAddress?.address_line_1 && {
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    cursor: 'not-allowed',
                  },
                  '& .MuiInputBase-root': {
                    backgroundColor: '#f5f5f5',
                  },
                }),
              }}
            />

            <TextField
              label={t('settings.addresses.fields.address2')}
              placeholder={t('settings.addresses.fields.address2.ph')}
              value={newAddress.address_line_2}
              onChange={(e) => setNewAddress({ ...newAddress, address_line_2: e.target.value })}
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('settings.addresses.fields.city')}
                value={newAddress.city}
                onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                fullWidth
                required
                disabled={readonlyFields && !!prefilledAddress?.city}
                sx={{
                  ...(readonlyFields && prefilledAddress?.city && {
                    '& .MuiInputBase-input': {
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed',
                    },
                    '& .MuiInputBase-root': {
                      backgroundColor: '#f5f5f5',
                    },
                  }),
                }}
              />
              <TextField
                label={t('settings.addresses.fields.state')}
                value={newAddress.state}
                onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                fullWidth
                required
                disabled={readonlyFields && !!prefilledAddress?.state}
                sx={{
                  ...(readonlyFields && prefilledAddress?.state && {
                    '& .MuiInputBase-input': {
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed',
                    },
                    '& .MuiInputBase-root': {
                      backgroundColor: '#f5f5f5',
                    },
                  }),
                }}
              />
              <TextField
                label={t('settings.addresses.fields.postalCode')}
                value={newAddress.postal_code}
                onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                fullWidth
                required
                disabled={readonlyFields && !!prefilledAddress?.postal_code}
                sx={{
                  ...(readonlyFields && prefilledAddress?.postal_code && {
                    '& .MuiInputBase-input': {
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed',
                    },
                    '& .MuiInputBase-root': {
                      backgroundColor: '#f5f5f5',
                    },
                  }),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('settings.addresses.fields.country')}
                value={newAddress.country}
                onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })}
                fullWidth
                required
                disabled={readonlyFields && !!prefilledAddress?.country}
                sx={{
                  ...(readonlyFields && prefilledAddress?.country && {
                    '& .MuiInputBase-input': {
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed',
                    },
                    '& .MuiInputBase-root': {
                      backgroundColor: '#f5f5f5',
                    },
                  }),
                }}
              />
              <TextField
                label={t('settings.addresses.fields.phone')}
                value={newAddress.phone}
                onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                fullWidth
              />
            </Box>

            {/* Coordinates Display */}
            {coordinates && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
              }}>
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                  📍 {t('settings.addresses.coords.title')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label={t('settings.addresses.coords.latitude')}
                    value={coordinates.lat.toFixed(6)}
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        cursor: 'not-allowed',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                      },
                      '& .MuiInputBase-root': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  />
                  <TextField
                    label={t('settings.addresses.coords.longitude')}
                    value={coordinates.lng.toFixed(6)}
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        cursor: 'not-allowed',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                      },
                      '& .MuiInputBase-root': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('settings.addresses.coords.note')}
                </Typography>
              </Box>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={newAddress.is_default}
                  onChange={(e) => setNewAddress({ ...newAddress, is_default: e.target.checked })}
                />
              }
              label={t('settings.addresses.switch.default')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setAddressValidatedViaMap(false);
          }}>{t('settings.addresses.dialog.cancel')}</Button>
          <Button 
            onClick={handleAddAddress} 
            variant="contained"
            disabled={addingAddress}
            startIcon={addingAddress ? <CircularProgress size={20} sx={{ color: 'white' }} /> : undefined}
            sx={{
              background: (coordinates || addressValidatedViaMap || readonlyFields)
                ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: (coordinates || addressValidatedViaMap || readonlyFields)
                  ? 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)'
                  : 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e',
                cursor: 'not-allowed',
              },
            }}
          >
            {addingAddress 
              ? t('settings.addresses.dialog.add.saving') 
              : (coordinates || addressValidatedViaMap || readonlyFields) 
                ? t('settings.addresses.dialog.add.verified') 
                : t('settings.addresses.dialog.add.submit')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('settings.addresses.dialog.edit.title')}
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => {
              if (editingAddress) {
                const randomAddress = generateRandomAddressData();
                setEditingAddress({
                  ...editingAddress,
                  ...randomAddress,
                });
                toast.success(t('settings.addresses.toast.randomGenerated'));
              }
            }}
            sx={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              color: '#667eea',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                border: '1px solid rgba(102, 126, 234, 0.5)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Generate Random
          </Button>
        </DialogTitle>
        <DialogContent>
          {editingAddress && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label={t('settings.addresses.fields.addressTitle')}
                value={editingAddress.title}
                onChange={(e) => setEditingAddress({ ...editingAddress, title: e.target.value })}
                fullWidth
                required
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={t('settings.addresses.fields.fullName')}
                  value={editingAddress.full_name}
                  onChange={(e) => setEditingAddress({ ...editingAddress, full_name: e.target.value })}
                  fullWidth
                  required
                />
                <TextField
                  label={t('settings.addresses.fields.company')}
                  value={editingAddress.company}
                  onChange={(e) => setEditingAddress({ ...editingAddress, company: e.target.value })}
                  fullWidth
                />
              </Box>

              <TextField
                label={t('settings.addresses.fields.address1')}
                value={editingAddress.address_line_1}
                onChange={(e) => setEditingAddress({ ...editingAddress, address_line_1: e.target.value })}
                fullWidth
                required
              />

              <TextField
                label={t('settings.addresses.fields.address2')}
                value={editingAddress.address_line_2}
                onChange={(e) => setEditingAddress({ ...editingAddress, address_line_2: e.target.value })}
                fullWidth
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={t('settings.addresses.fields.city')}
                  value={editingAddress.city}
                  onChange={(e) => setEditingAddress({ ...editingAddress, city: e.target.value })}
                  fullWidth
                  required
                />
                <TextField
                  label={t('settings.addresses.fields.state')}
                  value={editingAddress.state}
                  onChange={(e) => setEditingAddress({ ...editingAddress, state: e.target.value })}
                  fullWidth
                  required
                />
                <TextField
                  label={t('settings.addresses.fields.postalCode')}
                  value={editingAddress.postal_code}
                  onChange={(e) => setEditingAddress({ ...editingAddress, postal_code: e.target.value })}
                  fullWidth
                  required
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={t('settings.addresses.fields.country')}
                  value={editingAddress.country}
                  onChange={(e) => setEditingAddress({ ...editingAddress, country: e.target.value })}
                  fullWidth
                  required
                />
                <TextField
                  label={t('settings.addresses.fields.phone')}
                  value={editingAddress.phone}
                  onChange={(e) => setEditingAddress({ ...editingAddress, phone: e.target.value })}
                  fullWidth
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={editingAddress.is_default}
                    onChange={(e) => setEditingAddress({ ...editingAddress, is_default: e.target.checked })}
                  />
                }
                label={t('settings.addresses.switch.default')}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('settings.addresses.dialog.cancel')}</Button>
          <Button 
            onClick={handleEditAddress} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            {t('settings.addresses.dialog.edit.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Search for Address Validation */}
      <MapSearchNew
        open={mapSearchOpen}
        onClose={() => {
          setMapSearchOpen(false);
          setPendingAddress(null);
        }}
        onLocationSelect={handleMapSearchValidation}
        selectedLocations={[]}
        initialSearchQuery={pendingAddress ? 
          `${pendingAddress.address_line_1}, ${pendingAddress.city}, ${pendingAddress.state} ${pendingAddress.postal_code}, ${pendingAddress.country}` 
          : undefined
        }
        addressMode={true}
        addressData={pendingAddress ? {
          title: pendingAddress.title || '',
          full_name: pendingAddress.full_name || '',
          company: pendingAddress.company || '',
          address_line_1: pendingAddress.address_line_1 || '',
          address_line_2: pendingAddress.address_line_2 || '',
          city: pendingAddress.city || '',
          state: pendingAddress.state || '',
          postal_code: pendingAddress.postal_code || '',
          country: pendingAddress.country || '',
          phone: pendingAddress.phone || '',
        } : undefined}
      />
    </Box>
  );
};

export default AddressesTab;
